<template>
  <div v-if="flatNodes.length" class="sidebar-list">
    <div
      v-for="node in filteredNodes"
      :key="getNodeId(node.node)"
      class="sidebar-item"
      data-testid="vbudget-tree-node"
      :class="{ active: selectedNodeId === getNodeId(node.node), root: node.depth === 0 }"
      :style="{ paddingLeft: node.depth * 20 + 16 + 'px' }"
      @click="emit('update:selectedNodeId', getNodeId(node.node))">
      <template v-if="node.depth > 0">
        <div
          v-for="(active, lineIdx) in node.activeLines"
          :key="lineIdx"
          class="tree-line"
          :class="{ 'tree-line-hidden': !active }"
          :style="{ left: lineIdx * 20 + 26 + 'px' }" />
        <div
          class="tree-connector"
          :class="{ 'tree-connector-last': node.isLastChild }"
          :style="{ left: (node.depth - 1) * 20 + 26 + 'px' }" />
      </template>
      <div v-if="node.hasChildren" class="tree-parent-line" :style="{ left: node.depth * 20 + 26 + 'px' }" />
      <div class="d-flex align-items-center gap-2 min-w-0 flex-grow-1">
        <div class="sidebar-icon flex-shrink-0">
          <IconPigMoney v-if="node.depth === 0" size="18" />
          <IconWallet v-else size="18" />
        </div>
        <div class="min-w-0 flex-grow-1">
          <div
            class="fw-medium text-truncate"
            :class="{ 'fw-bold': node.depth === 0 }"
            data-testid="vbudget-tree-node-name">
            {{ getNodeName(node.node) }}
          </div>
          <small class="text-muted">
            {{ formatCurrency(node.node.availability.available_now, getNodeCurrency(node.node)) }}
          </small>
          <div class="d-flex align-items-center gap-2 mt-1">
            <div class="progress flex-grow-1" style="height: 3px">
              <div
                class="progress-bar"
                :class="getUsageColorClass(node.node)"
                :style="{ width: getUsagePercent(node.node) + '%' }"></div>
            </div>
            <small class="text-muted flex-shrink-0" style="font-size: 11px">{{ getUsagePercent(node.node) }}%</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IconWallet, IconPigMoney } from '@tabler/icons-vue';

import { formatCurrency } from '@/locale/format';
import {
  getBudgetNodeId,
  getBudgetNodeName,
  getBudgetNodeCurrency,
  getBudgetUsagePercent,
  getBudgetUsageColorClass,
} from '@/helpers/budgetUtils';

import type { IBudgetTree, IBudgetTreeNode } from '@/types/budget';

interface Props {
  tree: IBudgetTree | undefined;
  selectedNodeId: string | null;
  searchQuery: string;
  currency: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:selectedNodeId': [id: string];
}>();

/* -------------------------------- HELPERS -------------------------------- */
const getNodeId = getBudgetNodeId;
const getNodeName = getBudgetNodeName;
const getUsagePercent = getBudgetUsagePercent;
const getUsageColorClass = getBudgetUsageColorClass;

function getNodeCurrency(node: IBudgetTreeNode): string {
  return getBudgetNodeCurrency(node, props.currency);
}

/* -------------------------------- COMPUTED -------------------------------- */
interface FlatNode {
  node: IBudgetTreeNode;
  depth: number;
  isLastChild: boolean;
  hasChildren: boolean;
  activeLines: boolean[];
}

const flatNodes = computed<FlatNode[]>(() => {
  if (!props.tree?.tree) return [];

  const result: FlatNode[] = [];
  function flatten(node: IBudgetTreeNode, depth: number, isLastChild: boolean, activeLines: boolean[]) {
    const children = node.children || [];
    result.push({ node, depth, isLastChild, hasChildren: children.length > 0, activeLines: [...activeLines] });
    const nextActiveLines = depth > 0 ? [...activeLines, !isLastChild] : [...activeLines];
    children.forEach((child, i) => {
      flatten(child as IBudgetTreeNode, depth + 1, i === children.length - 1, nextActiveLines);
    });
  }
  flatten(props.tree.tree, 0, true, []);
  return result;
});

const filteredNodes = computed(() => {
  if (!props.searchQuery.trim()) return flatNodes.value;

  const q = props.searchQuery.toLowerCase();
  return flatNodes.value.filter(({ node }) => {
    return getNodeName(node).toLowerCase().includes(q);
  });
});
</script>

<style scoped>
.sidebar-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: background-color 0.15s;
  position: relative;
}

.sidebar-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.sidebar-item.active {
  background-color: rgba(var(--tblr-primary-rgb, 0, 122, 255), 0.06);
  border-right: 3px solid var(--tblr-primary, #007aff);
}

.sidebar-item.active .sidebar-icon {
  background-color: rgba(var(--tblr-primary-rgb, 0, 122, 255), 0.1);
  color: var(--tblr-primary, #007aff);
}

.tree-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px solid #d0d5dd;
}

.tree-line-hidden {
  border-left-color: transparent;
}

.tree-connector {
  position: absolute;
  top: 0;
  bottom: 0;
  border-left: 1px solid #d0d5dd;
}

.tree-connector::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  width: 12px;
  border-bottom: 1px solid #d0d5dd;
}

.tree-parent-line {
  position: absolute;
  top: 70%;
  bottom: 0;
  border-left: 1px solid #d0d5dd;
}

.tree-connector-last {
  bottom: 50%;
  border-bottom-left-radius: 4px;
  border-bottom: 1px solid #d0d5dd;
  width: 12px;
}

.tree-connector-last::after {
  display: none;
}

.sidebar-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  color: #9b9b9b;
}

.min-w-0 {
  min-width: 0;
}
</style>
